/**
 * Date Selection Types
 * Defines types for date selection modes in request creation
 */

import { ShiftPreference } from './requestTypes';
import { SHIFT_PREFERENCE_LABELS } from '../constants/requestConstants';

/**
 * Available modes for date selection in request form
 * period: Select a date range (start and end dates)
 * single: Select a single date
 * shift: Select a shift for a specific date
 * loose_days: Select multiple individual dates
 */
export type DateSelectionMode = 'period' | 'single' | 'shift' | 'loose_days';

/**
 * Date selection mode configuration
 */
export interface DateSelectionModeConfig {
    id: DateSelectionMode;
    label: string;
    description: string;
}

/**
 * Date selection modes available
 */
export const DATE_SELECTION_MODES: DateSelectionModeConfig[] = [
    {
        id: 'period',
        label: 'Periode',
        description: 'Velg en datoområde (start og slutt)',
    },
    {
        id: 'single',
        label: 'Dato',
        description: 'Velg en enkelt dag',
    },
    {
        id: 'shift',
        label: 'Skift',
        description: 'Velg et skift for en bestemt dag',
    },
    {
        id: 'loose_days',
        label: 'Enkeltdager',
        description: 'Velg flere enkelt dager',
    },
];

/**
 * Shift definition for shift selection
 */
export interface Shift {
    id: ShiftPreference;
    name: string;
    startTime: string;
    endTime: string;
    labelKey: keyof typeof SHIFT_PREFERENCE_LABELS;
}

/**
 * Available shifts for selection
 * D = Morgen (Morning)
 * A = Ettermiddag (Afternoon)
 * N = Kveld (Night)
 * L = Helg (Weekend)
 * X = Annet (Other)
 */
export const AVAILABLE_SHIFTS: Shift[] = [
    {
        id: ShiftPreference.MORNING,
        name: 'Morgen',
        startTime: '06:00',
        endTime: '14:00',
        labelKey: 'MORNING',
    },
    {
        id: ShiftPreference.AFTERNOON,
        name: 'Ettermiddag',
        startTime: '14:00',
        endTime: '22:00',
        labelKey: 'AFTERNOON',
    },
    {
        id: ShiftPreference.NIGHT,
        name: 'Kveld',
        startTime: '22:00',
        endTime: '06:00',
        labelKey: 'NIGHT',
    },
    {
        id: ShiftPreference.WEEKEND,
        name: 'Helg',
        startTime: '00:00',
        endTime: '23:59',
        labelKey: 'WEEKEND',
    },
    {
        id: ShiftPreference.OTHER,
        name: 'Annet',
        startTime: '00:00',
        endTime: '23:59',
        labelKey: 'OTHER',
    },
];

/**
 * Shift selection value
 * Represents a shift selected for a specific date
 */
export interface ShiftSelectionValue {
    date: Date;
    shiftId: string;
}

/** Individual date selection types */
export type SingleDateSelection = { mode: 'single'; date: Date | null };
export type PeriodDateSelection = {
    mode: 'period';
    startDate: Date | null;
    endDate: Date | null;
};
export type LooseDaysDateSelection = { mode: 'loose_days'; dates: Date[] };
export type ShiftDateSelection = {
    mode: 'shift';
    shiftSelection: ShiftSelectionValue | null;
};

/**
 * Union type for all date selection values
 */
export type DateSelectionValue =
    | SingleDateSelection
    | PeriodDateSelection
    | LooseDaysDateSelection
    | ShiftDateSelection;
